import { http } from '@lib/api';
import type { OnfleetWorker } from '@/types/onfleet';
import type { DataTableParams } from '@resource-kit/controllers/react';
import type { ListPage } from './list-envelope';
import { toApiListParams } from './list-request';
import { DRIVER_FILTERS } from './filters.generated';

// Re-export the driver shape so callers can import everything they need
// from `@/services` rather than reaching into `@/types/onfleet` directly.
// #1405 single-source gap: a generated `Driver` component EXISTS, but its
// shape (flat `vehicle`/`metadata` JSON strings; no `display_name` /
// `tasks` / `is_on_duty` / activity fields) does not cover what the
// drivers resource renders, so binding it would break real reads — kept
// hand-written; reconciling the drivers read schema is a follow-up.
export type { OnfleetWorker } from '@/types/onfleet';

// The `/api/drivers` filter whitelist is codegen from the backend
// `DRIVER_LIST_CONFIG.filterable` via the OpenAPI `x-filterable` extension
// (issue #1407). Re-exported here so the `@/services/drivers` import path is
// unchanged; edit the backend config, not this file.
export { DRIVER_FILTERS };

export const driverAPI = {
  /**
   * Fetch a paginated drivers list. `toApiListParams` translates the
   * shared table state to the backend list kernel's query: `limit`→`size`,
   * and `extraParams` filters whitelisted against `DRIVER_FILTERS` (an
   * unsupported filter is dropped, not 400-d).
   *
   * The new backend (`GET /api/drivers`, #935) returns the `{ items,
   * total, page, size }` list body; the api-client unwraps the `data`
   * envelope and the shared list UI consumes that page verbatim (#1909).
   */
  list(params: DataTableParams): Promise<ListPage<OnfleetWorker>> {
    return http.get<ListPage<OnfleetWorker>>('/api/drivers', {
      params: toApiListParams(params, DRIVER_FILTERS),
    });
  },

  /**
   * Fetch one driver. Returns the bare record: `http.get` already strips the
   * `{ data }` wire envelope, and the sole reader (`driverResource.host.api.get`
   * in `features/drivers/schema.tsx`) wants exactly that shape.
   */
  detail(id: string): Promise<OnfleetWorker> {
    return http.get<OnfleetWorker>(`/api/drivers/${id}`);
  },

  // No `delete` method here — the backend route file exposes only the
  // read/list surface for drivers. Adding a service method that hits a
  // non-existent endpoint would be dead code at best and a runtime 404
  // at worst.
};
